import { ref, computed, type Ref, type ComputedRef } from "vue";
import { chunk } from "lodash";
import { Logger } from "@/logger";
import apiClient, { APIError } from "@/APIClient";
import { localDrawingRepository } from "@/repositories/local-drawing.repository";
import type { UploadProgressHook } from "@/stores/fileStore";
import {
  FileType,
  type DRDrawing,
  type DRLocalDrawingData,
  type DRLocalDrawingMeta,
} from "@/types";
import { drawingRepository } from "./drawing.repository";
import { queryClient } from "@/stores/queries/query-client";
import { QueryKey } from "@/stores/queries/query-keys";
import { drawingImageRepository } from "./drawingImage.repository";
import { activityEventRepository } from "./activityEvent.repository";
import type { DRActivityEvent } from "@/types/activity-event.types";
import { uploadFilesBatch } from "@/utils/upload-files.utils";
import { getDeviceFingerprint } from "@/utils/device-fingerprint";

// Date of the VSFX to DWG migration - VSFX files saved before this date need recovery
const MIGRATION_DATE = "15/03/2026";

// Oplog push (cf. pushActivityEvents): number of events per POST request. Large
// enough to limit round-trips when catching up on a big backlog (e.g. 66 then
// 313 events observed in prod on DR-API-24), small enough that the sequential
// fallback after a chunk failure (per-event isolation) stays fast in the worst
// case (= PUSH_CHUNK_SIZE individual requests).
export const PUSH_CHUNK_SIZE = 25;

// Oplog push: capped exponential backoff after a failed cycle. Affects ONLY
// pushActivityEvents — the Monitor's fixed 10s interval (fileStore.ts) that
// triggers autoSync() is NOT changed. Steps: 10s, 20s, 40s, 80s, 160s, then
// capped at 5 min.
export const PUSH_BACKOFF_BASE_MS = 10_000;
export const PUSH_BACKOFF_MAX_MS = 5 * 60_000;

function toActivityEventSyncPayload(e: DRActivityEvent) {
  return {
    eventId: e.eventId,
    revisionId: e.revisionId!, // getPushable() garantit revisionId !== null
    entityUid: e.entityUid,
    type: e.type,
    clientTs: new Date(e.clientTs).toISOString(),
    snapshot: e.snapshot,
  };
}

export type SyncTrackerStatus = "syncing" | "done" | "failed";

export interface SyncTracker {
  /** = String(drawingId) — un seul tracker actif par dessin à la fois. */
  id: string;
  drawingId: number;
  drawingName: string;
  status: SyncTrackerStatus;
  error?: string;
}

export class Syncer extends EventTarget {
  private syncingDrawingIds = new Set<number>();

  /** Oplog push: timestamp (epoch ms) before which pushActivityEvents() is a
   *  no-op — backoff after a failed cycle, cf. registerPushFailure(). */
  private nextPushAttemptAt = 0;

  /** Oplog push: number of CONSECUTIVE cycles with at least one failed chunk —
   *  used to compute the next backoff delay (reset as soon as a cycle succeeds
   *  entirely, cf. resetPushBackoff()). */
  private consecutiveFailedPushCycles = 0;

  /**
   * Un tracker par dessin en cours d'upload (ou dont la dernière tentative a
   * échoué) — sert au toast "ne fermez pas l'application" (cf. SyncToast.vue).
   * Un succès se retire après un court délai (même comportement que les uploads
   * DWG, cf. fileStore.ts) ; un échec reste affiché jusqu'à fermeture manuelle
   * (`removeSyncTracker`) pour que l'utilisateur voie quel dessin a échoué.
   */
  public readonly syncTrackers: Ref<SyncTracker[]> = ref([]);

  /** Vrai s'il y a au moins un upload réel en cours — dérivé de syncTrackers. */
  public readonly isSyncing: ComputedRef<boolean> = computed(() =>
    this.syncTrackers.value.some((t) => t.status === "syncing")
  );

  private startSyncTracker(drawingId: number, drawingName: string): string {
    const id = String(drawingId);
    this.syncTrackers.value = [
      ...this.syncTrackers.value.filter((t) => t.id !== id),
      { id, drawingId, drawingName, status: "syncing" },
    ];
    return id;
  }

  private finishSyncTracker(id: string): void {
    const tracker = this.syncTrackers.value.find((t) => t.id === id);
    if (!tracker) return;
    tracker.status = "done";
    setTimeout(() => this.removeSyncTracker(id), 3000);
  }

  private failSyncTracker(id: string, error: string): void {
    const tracker = this.syncTrackers.value.find((t) => t.id === id);
    if (!tracker) return;
    tracker.status = "failed";
    tracker.error = error;
  }

  removeSyncTracker(id: string): void {
    this.syncTrackers.value = this.syncTrackers.value.filter((t) => t.id !== id);
  }

  /**
   * Recharge le dessin COMPLET (binaires inclus) pour les seules branches qui en ont
   * besoin — `createNewDrawing` et `saveRevision` uploadent le draft. Le tri ci-dessus
   * travaille sur des métadonnées : charger les buffers des N dessins locaux à chaque
   * tick (toutes les 10 s) pour n'en uploader qu'un ou deux était le gros du coût.
   * `getById` et non `getByIdOrThrow` : le dessin a pu être supprimé entre le tri et
   * ici, ce n'est pas une erreur.
   */
  private async withFullDrawing<T>(
    meta: DRLocalDrawingMeta,
    run: (local: DRLocalDrawingData) => Promise<T>
  ): Promise<T | void> {
    const local = await localDrawingRepository.getById(meta.id);
    if (!local) return;
    return run(local);
  }

  async autoSync() {
    const localDrawings = await localDrawingRepository.fetchAllMetaByNewest();

    if (!localDrawings.length) {
      return;
    }

    const onError: DRLocalDrawingMeta[] = [];
    const newDrawings: DRLocalDrawingMeta[] = []; // negative ID
    const withDraft: DRLocalDrawingMeta[] = []; // has draft (unsaved changes)
    const others: DRLocalDrawingMeta[] = []; // need server comparison

    localDrawings.forEach((local) => {
      if (local.error) {
        onError.push(local);
        return;
      }
      if (local.id < 0) {
        newDrawings.push(local);
        return;
      }
      // Check for both DWG and VSFX drafts
      if (local.lastRevision.hasDwgDraft || local.lastRevision.hasDraft) {
        withDraft.push(local);
        return;
      }
      others.push(local);
    });

    const res = await Promise.all([
      ...onError.map(() => Promise.resolve()),
      ...newDrawings.map((meta) =>
        this.withFullDrawing(meta, (local) => this.createNewDrawing(local))
      ),
      ...withDraft.map((meta) =>
        this.withFullDrawing(meta, (local) => this.saveRevision(local))
      ),
      this.syncOtherDrawings(others),
    ]);

    // Oplog : pousse les events bindés à une révision par les saves ci-dessus
    // (+ tout reliquat d'un push précédent en échec).
    await this.pushActivityEvents();

    if (res.some((r) => r === 1)) {
      // refresh drawing data
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
    }

    await this.removeUnusedImagesInDb();
  }

  /**
   * Taille des lots d'ids envoyés au serveur. Un dessin local absent de la
   * réponse est RETIRÉ du stockage local (archivé ou supprimé côté serveur) :
   * la réponse doit donc être exhaustive pour les ids demandés, ce qu'une seule
   * requête plafonnée à `take` ne garantit pas au-delà de `take` dessins.
   */
  private static readonly SYNC_OTHERS_BATCH_SIZE = 100;

  async syncOtherDrawings(others: DRLocalDrawingMeta[]) {
    if (!others.length) {
      return;
    }

    const serverDrawings: DRDrawing[] = [];
    for (const batch of chunk(others, Syncer.SYNC_OTHERS_BATCH_SIZE)) {
      serverDrawings.push(
        ...(await apiClient.fetchDrawings({
          archived: false,
          ids: batch.map(({ id }) => id),
          skip: 0,
          // Strictement supérieur à la taille du lot : la réponse ne peut pas
          // être tronquée.
          take: Syncer.SYNC_OTHERS_BATCH_SIZE + 50,
          sortBy: "createdAt",
          order: "desc",
        }))
      );
    }

    await drawingRepository.createMany(serverDrawings);

    await Promise.all(
      others.map(async (local) => {
        const server = serverDrawings.find((s) => s.id === local.id);

        if (server) {
          if (local.lastRevision.id !== server.lastRevision.id) {
            await drawingRepository.replace(server);
            await this.makeDrawingLocal(local.id);
          } else {
            // up to date
          }
        } else {
          await localDrawingRepository.deleteById(local.id);
          await drawingRepository.deleteById(local.id);
        }
      })
    );
  }

  /**
   * Purge les images dont le dessin n'existe plus localement (ex. upload d'un dessin
   * neuf qui n'a pas rattaché l'image à son id serveur, cf. `uploadNewImages`).
   *
   * Une différence d'ensembles d'entiers, entièrement servie par les index : ni les
   * binaires DWG ni les octets des images ne sont désérialisés. Ça compte parce que
   * `autoSync` appelle cette méthode toutes les 10 s, et que les images sont stockées
   * BRUTES (cf. `useImageImport.insertImage` — pas de compression, photos d'appareil
   * incluses) : les charger pour lire un `drawingId` était du travail entièrement
   * gratuit.
   */
  async removeUnusedImagesInDb(): Promise<void> {
    const localIds = new Set(await localDrawingRepository.getAllIds());
    const withImages = await drawingImageRepository.getDrawingIdsWithImages();
    const orphans = withImages.filter((id) => !localIds.has(id));
    await drawingImageRepository.deleteByDrawingIds(orphans);
  }

  // ************************************
  //         SYNC DRAWING METHODS
  // ************************************

  /**
   * Save a revision to the server.
   * Handles both DWG (S3 storage) and VSFX (legacy - triggers recovery).
   */
  async saveRevision(localDrawing: DRLocalDrawingData): Promise<number | void> {
    const drawingId = localDrawing.id;

    if (this.syncingDrawingIds.has(drawingId)) return;
    this.syncingDrawingIds.add(drawingId);

    try {
      return await this._saveRevisionInner(localDrawing);
    } finally {
      this.syncingDrawingIds.delete(drawingId);
    }
  }

  private async _saveRevisionInner(
    localDrawing: DRLocalDrawingData
  ): Promise<number | void> {
    const drawingId = localDrawing.id;
    const drawing = await drawingRepository.getByIdOrThrow(drawingId);

    const {
      dwgDraft,
      draft: draftVsfx,
      draftPaths: draftRasterPaths,
      id: lastRevisionId,
      branchId,
    } = localDrawing.lastRevision;

    // Check for DWG draft first (new format), then VSFX (legacy)
    const hasDwgDraft = !!dwgDraft;
    const draft = dwgDraft || draftVsfx;

    if (!draft || draft.byteLength === 0) {
      Logger.error(`Cannot save revision: draft is empty or 0 bytes`);
      return;
    }

    // VSFX drafts are legacy files that need recovery (ODA is no longer available)
    if (!hasDwgDraft && draftVsfx) {
      return this.handleVsfxRecovery(localDrawing, draftVsfx, branchId);
    }

    const trackerId = this.startSyncTracker(drawingId, drawing.name);

    try {
      const device = getDeviceFingerprint();

      // DWG format: upload to S3
      const uploadResults = await uploadFilesBatch([
        {
          file: new File([dwgDraft!], `${drawing.name}.dwg`, {
            type: "application/octet-stream",
          }),
          fileType: FileType.REVISION_DWG,
          params: { drawingId },
        },
      ]);

      const fileId = uploadResults[0]?.fileId;
      const uploadSucceeded = uploadResults[0]?.success;
      if (!fileId || !uploadSucceeded) {
        throw new Error("Failed to upload DWG file to S3");
      }

      const result = await apiClient.createRevision(
        drawingId,
        null, // no openCloudFileId for DWG
        "DWG",
        lastRevisionId,
        draftRasterPaths,
        device,
        fileId
      );
      const updatedDrawing = result.drawing;

      // Oplog : rattache les events pending à la révision qui vient d'être créée
      // (leur `revisionId` passe de null à la nouvelle révision).
      await activityEventRepository.bindPendingToRevision(
        drawingId,
        updatedDrawing.lastRevision.id
      );

      await this.uploadNewImages(drawingId);

      await drawingRepository.replace(updatedDrawing);

      // Save the revision locally and clear the draft
      await localDrawingRepository.saveLastRevisionDwg(
        updatedDrawing,
        dwgDraft!
      );

      await this.syncDrawingImages(drawingId, draftRasterPaths);
      this.finishSyncTracker(trackerId);
      return 1;
    } catch (e: any) {
      const errorMessage = e?.message || "Unknown error";
      Logger.vsfError(
        `Syncer.saveRevision() : error while saving revision - ${errorMessage}`,
        drawingId,
        draft
      );
      localDrawingRepository.setDrawingError(drawingId, errorMessage);
      this.failSyncTracker(trackerId, errorMessage);
    }
  }

  /**
   * Handle legacy VSFX files that couldn't be synced.
   * Sends the file to the recovery endpoint and shows a message to the user.
   */
  private async handleVsfxRecovery(
    localDrawing: DRLocalDrawingData,
    vsfxDraft: ArrayBuffer,
    branchId?: number
  ): Promise<void> {
    const drawingId = localDrawing.id;

    try {
      Logger.info(
        `Syncer.handleVsfxRecovery() : sending VSFX to recovery for drawing ${drawingId}`
      );

      // Send to recovery endpoint
      await apiClient.submitVsfxRecovery(drawingId, branchId || 0, vsfxDraft);

      // Mark as pending recovery with a clear message
      const recoveryMessage = `Votre fichier date d'avant la mise à jour du ${MIGRATION_DATE}. L'équipe DiagnosticRapid s'occupe de le réparer. Vous serez notifié par email une fois le fichier restauré.`;

      localDrawingRepository.setDrawingError(drawingId, recoveryMessage);

      Logger.info(
        `Syncer.handleVsfxRecovery() : VSFX sent to recovery successfully for drawing ${drawingId}`
      );
    } catch (e: any) {
      const errorMessage = `Impossible d'envoyer le fichier pour récupération: ${
        e?.message || "Erreur inconnue"
      }`;
      Logger.error(
        `Syncer.handleVsfxRecovery() : error sending VSFX to recovery for drawing ${drawingId} - ${errorMessage}`
      );
      localDrawingRepository.setDrawingError(drawingId, errorMessage);
    }
  }

  /**
   * Create a new drawing on the server.
   * Handles DWG (S3 storage). VSFX drafts trigger recovery flow.
   */
  async createNewDrawing(
    localDrawing: DRLocalDrawingData
  ): Promise<number | void> {
    async function removeNewDrawingFromLocal() {
      await localDrawingRepository.removeTemporaryDrawing(localDrawing.id);
      await drawingRepository.deleteById(drawingId);
    }
    const drawingId = localDrawing.id;
    if (drawingId >= 0) {
      Logger.warn(
        `Syncer.createNewDrawing() : drawingId should be negative, skipping creation on server`
      );
      return;
    }

    const drawing = await drawingRepository.getByIdOrThrow(drawingId);
    const {
      dwgDraft,
      draft: draftVsfx,
      draftPaths: draftRasterPaths,
    } = localDrawing.lastRevision;

    // Check for DWG draft first (new format), then VSFX (legacy)
    const hasDwgDraft = !!dwgDraft;
    const draft = dwgDraft || draftVsfx;

    if (!draft) {
      Logger.info(
        `Syncer.createNewDrawing() : remove created drawing as no draft is available for drawing ${drawingId}`
      );
      await removeNewDrawingFromLocal();
      return;
    }

    // VSFX drafts for new drawings cannot be recovered (no server-side drawing exists yet)
    // Show error message explaining the situation
    if (!hasDwgDraft && draftVsfx) {
      const errorMessage = `Ce dessin a été créé avant la mise à jour du ${MIGRATION_DATE} et ne peut pas être synchronisé. Veuillez contacter l'équipe DiagnosticRapid pour assistance.`;
      Logger.error(
        `Syncer.createNewDrawing() : VSFX draft for new drawing ${drawingId} cannot be recovered`
      );
      localDrawingRepository.setDrawingError(drawingId, errorMessage);
      return;
    }

    const trackerId = this.startSyncTracker(drawingId, drawing.name);

    try {
      // DWG format: upload to S3
      const uploadResults = await uploadFilesBatch([
        {
          file: new File([dwgDraft!], `${drawing.name}.dwg`, {
            type: "application/octet-stream",
          }),
          fileType: FileType.REVISION_DWG,
          params: { drawingId: 0 }, // New drawing, no ID yet
        },
      ]);

      const fileId = uploadResults[0]?.fileId;
      const uploadSucceeded = uploadResults[0]?.success;
      if (!fileId || !uploadSucceeded) {
        throw new Error("Failed to upload DWG file to S3");
      }

      const newDrawing = await apiClient.createDrawing(
        drawing.name,
        drawing.projectId,
        {
          firstRevisionFileId: fileId,
          rasterPaths: draftRasterPaths,
        }
      );

      // Oplog : dessin neuf créé côté serveur → rattache ses events pending à la
      // 1ère révision, ET remappe leur drawingId (tempId négatif → id serveur) pour
      // rester cohérent partout (défensif).
      await activityEventRepository.bindPendingToRevision(
        drawingId,
        newDrawing.lastRevision.id
      );
      await activityEventRepository.remapDrawingId(drawingId, newDrawing.id);

      await this.uploadNewImages(drawingId, newDrawing.id);
      await drawingRepository.createSafeById(newDrawing);

      // Save the drawing locally with the file we already have
      await localDrawingRepository.saveLastRevisionDwg(newDrawing, dwgDraft!);

      await removeNewDrawingFromLocal();
      this.finishSyncTracker(trackerId);
      return 1;
    } catch (e: any) {
      const errorMessage =
        e?.message || "Une erreur est survenue lors de la sauvegarde de la révision.";
      Logger.vsfError(
        `Syncer.createNewDrawing() : error while saving draft as duplicate `,
        drawing.id,
        draft
      );
      localDrawingRepository.setDrawingError(drawing.id, errorMessage);
      this.failSyncTracker(trackerId, errorMessage);
    }
  }

  // ************************************
  //         SYNC IMAGE METHODS
  // ************************************
  async uploadNewImages(drawingId: number, targetDrawingId?: number) {
    const imageRecords = await drawingImageRepository.getByDrawingId(drawingId);
    const newImagesRecords = imageRecords.filter((record) => record.id < 0);
    if (newImagesRecords.length === 0) return;

    const uploadingImagesPath = Array.from(
      new Set([...newImagesRecords.map((record) => record.rasterPath)])
    );

    await uploadFilesBatch(
      newImagesRecords.map((image) => ({
        file: new File([image.data], image.originalName, {
          type: image.mimeType,
        }),
        fileType: FileType.DRAWING_IMAGE,
        params: {
          drawingId: targetDrawingId ?? drawingId,
          rasterPath: image.rasterPath,
        },
      }))
    );

    const serverDrawingImages = await apiClient.fetchDrawingImages({
      rasterPaths: uploadingImagesPath,
      drawingId: targetDrawingId ?? drawingId,
    });

    await Promise.all(
      serverDrawingImages.map(async (serverImage) => {
        const record = newImagesRecords.find(
          (record) => record.rasterPath === serverImage.rasterPath
        );
        if (!record) return;
        await drawingImageRepository.replaceNewRecordWithServerImage(
          record,
          serverImage
        );
      })
    );
  }

  // ************************************
  //         MAKING LOCAL
  // ************************************

  /**
   * Download the drawing file and store it locally.
   * For VSFX format: download from OCS (legacy)
   * For DWG format: download from S3
   */
  async makeDrawingFileLocal(drawingId: number) {
    try {
      const drawing = await drawingRepository.getByIdOrThrow(drawingId);
      const { format, openCloudFileId, fileId } = drawing.lastRevision;

      if (format === "DWG") {
        // DWG format: download from S3
        if (!fileId) {
          Logger.warn(
            `Syncer.makeDrawingFileLocal() : DWG revision has no fileId for drawing ${drawingId}`
          );
          return;
        }
        await this.makeDwgLocal(drawing);
      } else {
        // VSFX/VSF format: download from OCS (legacy)
        if (!openCloudFileId) {
          Logger.warn(
            `Syncer.makeDrawingFileLocal() : VSFX revision has no openCloudFileId for drawing ${drawingId}`
          );
          return;
        }
        await this.makeVsfxLocal(drawing);
      }
    } catch {
      Logger.error(
        `Syncer.makeDrawingFileLocal() : error while making last revision local for drawing ${drawingId}`
      );
      localDrawingRepository.setDrawingError(
        drawingId,
        "Une erreur est survenue lors de la récupération de la dernière révision."
      );
    }
  }

  /**
   * Download VSFX file from OCS and store locally.
   * @deprecated - ODA is no longer available. This will show an error message.
   */
  private async makeVsfxLocal(drawing: DRDrawing) {
    // ODA is no longer available - show error message to user
    const errorMessage = `Ce dessin utilise un ancien format (VSFX) qui n'est plus supporté depuis la mise à jour du ${MIGRATION_DATE}. Veuillez contacter l'équipe DiagnosticRapid pour assistance.`;
    Logger.error(
      `Syncer.makeVsfxLocal() : VSFX format no longer supported for drawing ${drawing.id}`
    );
    localDrawingRepository.setDrawingError(drawing.id, errorMessage);
    throw new Error(errorMessage);
  }

  /**
   * Download DWG file from S3 and store locally.
   */
  private async makeDwgLocal(drawing: DRDrawing) {
    const { url } = await apiClient.getRevisionDownloadUrl(
      drawing.id,
      drawing.lastRevision.id
    );

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download DWG: ${response.statusText}`);
    }

    const dwgBuffer = await response.arrayBuffer();
    await localDrawingRepository.saveLastRevisionDwg(drawing, dwgBuffer);
  }

  async syncDrawingImages(drawingId: number, rasterPaths?: string[]) {
    if (!rasterPaths?.length) return;
    try {
      const imageRecords = await drawingImageRepository.getByDrawingId(
        drawingId
      );

      const localPaths = imageRecords.map((record) => record.rasterPath);
      const pathsToDownload = rasterPaths.filter(
        (path) => !localPaths.includes(path)
      );
      const pathsToRemove = localPaths.filter(
        (path) => !rasterPaths.includes(path)
      );

      // to remove from idb
      await Promise.all(
        pathsToRemove.map(async (path) => {
          const record = imageRecords.find(
            (record) => record.rasterPath === path
          );
          if (record) {
            await drawingImageRepository.deleteById(record.id);
          }
        })
      );

      // to store on idb
      if (!pathsToDownload.length) return;

      const drawingImagesToDownload = await apiClient.fetchDrawingImages({
        drawingId,
        rasterPaths: pathsToDownload,
      });

      await Promise.all(
        drawingImagesToDownload.map(async (image) => {
          const file = image.file;
          if (!file) {
            Logger.error(
              `Syncer.makeRevisionImagesLocal() : image file metadata missing for drawing ${drawingId} and rasterPath ${image.rasterPath}`
            );
            return;
          }
          const blob = await apiClient.downloadFile(file.id);
          const imageData = new Uint8Array(await blob.arrayBuffer());

          await drawingImageRepository.upsert({
            id: image.id,
            rasterPath: image.rasterPath,
            drawingId,
            data: imageData,
            originalName: file.originalName,
            mimeType: file.mimeType,
            size: file.size,
          });
        })
      );
    } catch {
      Logger.error(
        `Syncer.makeRevisionImagesLocal() : error while making revision images local `
      );
    }
  }

  async makeImagesLocal(drawingId: number) {
    const localDrawing = await localDrawingRepository.getByIdOrThrow(drawingId);

    const rasterPaths = localDrawing.lastRevision.rasterPaths;

    if (!rasterPaths?.length) return;

    localDrawing.lastRevision.draftPaths = [...rasterPaths];
    await localDrawingRepository.replace(localDrawing);

    await this.syncDrawingImages(drawingId, rasterPaths);
  }

  async makeDrawingLocal(drawingId: number) {
    try {
      await this.makeDrawingFileLocal(drawingId);
      await this.makeImagesLocal(drawingId);
      await this.makeOplogLocal(drawingId);
    } catch (e: any) {
      Logger.error(
        `Syncer.makeDrawingLocal : could not make file local ${drawingId}`
      );
      throw e;
    }
  }

  /**
   * Oplog: pushes events bound to a revision and not yet synced, in batches of
   * `PUSH_CHUNK_SIZE`. Idempotent server-side (atomic dedup by eventId, cf.
   * activity-events.service.ts): a network failure is harmless, retried on the
   * next cycle.
   *
   * Isolation: if a chunk fails as a whole (e.g. ONE invalid event fails the
   * entire server-side `createMany`), its events are retried one by one to
   * isolate only the culprit(s) rather than blocking the whole batch forever
   * (cf. `pushChunkItemsIndividually`). An event that keeps failing this way for
   * `ACTIVITY_EVENT_FAILURE_THRESHOLD` distinct cycles is excluded from
   * `getPushable()` — never deleted — and reported to Sentry for investigation.
   *
   * Backoff: after a cycle with at least one failed chunk, subsequent calls are
   * a no-op until `nextPushAttemptAt` (exponential, capped at
   * `PUSH_BACKOFF_MAX_MS`, reset as soon as a full cycle succeeds). Affects ONLY
   * this method: fileStore.ts's Monitor keeps calling autoSync() every 10s,
   * pushActivityEvents() alone decides whether to act on a given tick.
   */
  async pushActivityEvents(): Promise<void> {
    if (Date.now() < this.nextPushAttemptAt) return;

    const pushable = await activityEventRepository.getPushable();
    if (!pushable.length) {
      this.resetPushBackoff();
      return;
    }

    let anyChunkFailed = false;

    for (const events of chunk(pushable, PUSH_CHUNK_SIZE)) {
      try {
        await this.pushChunk(events);
      } catch (e) {
        anyChunkFailed = true;
        if (this.isNetworkError(e)) {
          Logger.warn(
            `Syncer.pushActivityEvents : réseau indisponible, push interrompu (${pushable.length} event(s) au total en attente)`
          );
          break;
        }
        const networkDown = await this.pushChunkItemsIndividually(events);
        if (networkDown) break;
      }
    }

    if (anyChunkFailed) {
      this.registerPushFailure();
    } else {
      this.resetPushBackoff();
    }
  }

  /** One chunk pushed in a single POST — all-or-nothing (cf. pushActivityEvents'
   *  class doc). */
  private async pushChunk(events: DRActivityEvent[]): Promise<void> {
    await apiClient.syncActivityEvents(events.map(toActivityEventSyncPayload));
    await activityEventRepository.markSynced(events.map((e) => e.eventId));
  }

  /**
   * Sequential fallback after a chunk failure: one POST per event to precisely
   * isolate the culprit(s) (cf. class doc). @returns true if a network/offline
   * error interrupted the loop (the caller should then stop the remaining
   * chunks too — no point pushing further).
   */
  private async pushChunkItemsIndividually(
    events: DRActivityEvent[]
  ): Promise<boolean> {
    for (const event of events) {
      try {
        await apiClient.syncActivityEvents([toActivityEventSyncPayload(event)]);
        await activityEventRepository.markSynced([event.eventId]);
      } catch (e) {
        if (this.isNetworkError(e)) {
          Logger.warn(
            `Syncer.pushActivityEvents : réseau indisponible pendant l'isolation, event ${event.eventId} retenté au prochain cycle`
          );
          return true;
        }
        await this.handleEventPushFailure(event, e);
      }
    }
    return false;
  }

  /** Rejection CONFIRMED by the server for an individually-pushed event: counts
   *  the failure and, at the threshold, excludes it + alerts Sentry (cf. class
   *  doc). */
  private async handleEventPushFailure(
    event: DRActivityEvent,
    e: unknown
  ): Promise<void> {
    const status = e instanceof APIError ? e.status : undefined;
    const body = e instanceof APIError ? e.response : undefined;

    const { failureCount, excluded } =
      await activityEventRepository.recordPushFailure(event.eventId);

    Logger.warn(
      `Syncer.pushActivityEvents : échec du push pour l'event ${event.eventId} (drawing ${event.drawingId}, échec n°${failureCount}) - status ${status}`
    );

    if (excluded) {
      Logger.captureException(
        new Error(
          `Syncer.pushActivityEvents : event ${event.eventId} définitivement exclu de getPushable() après ${failureCount} échecs consécutifs`
        ),
        { event, apiError: { status, body } }
      );
    }
  }

  private isNetworkError(e: unknown): boolean {
    return e instanceof Error && APIError.isOffline(e);
  }

  private registerPushFailure(): void {
    this.consecutiveFailedPushCycles += 1;
    const delay = Math.min(
      PUSH_BACKOFF_BASE_MS * 2 ** (this.consecutiveFailedPushCycles - 1),
      PUSH_BACKOFF_MAX_MS
    );
    this.nextPushAttemptAt = Date.now() + delay;
  }

  private resetPushBackoff(): void {
    this.consecutiveFailedPushCycles = 0;
    this.nextPushAttemptAt = 0;
  }

  /**
   * Oplog : télécharge les events de la LIGNÉE du head (pas des branches sœurs) et
   * remplace le cache local synchronisé du dessin — best-effort. Appelé au download
   * ET au switch de branche (via makeDrawingLocal → syncDrawings). Le remplacement
   * (et non un simple ajout) est nécessaire : sans lui, les events d'une branche
   * quittée resteraient en IDB indéfiniment (contamination inter-branches).
   */
  async makeOplogLocal(drawingId: number): Promise<void> {
    try {
      const drawing = await drawingRepository.getById(drawingId);
      const headRevisionId = drawing?.lastRevision?.id;
      if (!headRevisionId || headRevisionId < 0) return;
      const dtos = await apiClient.getActivityEventsByLineage(headRevisionId);
      const events: DRActivityEvent[] = dtos.map((d) => ({
        eventId: d.eventId,
        drawingId,
        entityUid: d.entityUid,
        type: d.type,
        clientTs: new Date(d.clientTs).getTime(),
        snapshot: d.snapshot ?? undefined,
        revisionId: d.revisionId,
        synced: 1,
        localSaved: 1,
        failureCount: 0,
        excluded: 0,
      }));
      await activityEventRepository.replaceSyncedByDrawing(drawingId, events);
    } catch {
      Logger.warn(`Syncer.makeOplogLocal : could not fetch oplog for ${drawingId}`);
    }
  }

  // ************************************
  //         UPLOAD METHODS
  // ************************************

  /**
   * Upload multiple DWG files to S3 and create drawings.
   */
  async uploadManyDwg(
    domFiles: FileList | File[],
    projectId: number,
    onProgressCallbacks: Array<{
      onProgress: UploadProgressHook;
      onDrawingCreated: (drawing: DRDrawing) => void;
      onError: (error: string) => void;
    }>
  ) {
    const uploads = Array.from(domFiles).map((domFile, i) =>
      this.uploadOneDwg(domFile, projectId, onProgressCallbacks[i])
    );
    await Promise.all(uploads);
  }

  /**
   * Upload a single DWG file to S3 and create a drawing.
   */
  async uploadOneDwg(
    domFile: File,
    projectId: number,
    hooks: {
      onProgress: UploadProgressHook;
      onDrawingCreated: (drawing: DRDrawing) => void;
      onError: (error: string) => void;
    }
  ) {
    try {
      // Read file bytes before uploading (we'll store them locally after)
      const dwgBuffer = await domFile.arrayBuffer();

      // Upload to S3
      const uploadResults = await uploadFilesBatch(
        [
          {
            file: domFile,
            fileType: FileType.REVISION_DWG,
            params: { drawingId: 0 }, // New drawing
          },
        ],
        (index, percent) => {
          hooks.onProgress(percent / 100); // Convert to 0-1 range
        }
      );

      const fileId = uploadResults[0]?.fileId;
      const uploadSucceeded = uploadResults[0]?.success;
      if (!fileId || !uploadSucceeded) {
        throw new Error("Failed to upload DWG file");
      }

      // Create drawing with the uploaded file
      const fileName = domFile.name.replace(/\.dwg$/i, "");
      const drawing = await apiClient.createDrawing(fileName, projectId, {
        firstRevisionFileId: fileId,
      });

      // Store drawing metadata and file locally (without re-downloading)
      await drawingRepository.createSafeById(drawing);
      await localDrawingRepository.saveLastRevisionDwg(drawing, dwgBuffer);

      hooks.onDrawingCreated(drawing);
    } catch (e: any) {
      const errorMessage = e?.message || "Upload failed";
      Logger.error(`Syncer.uploadOneDwg() : ${errorMessage}`);
      hooks.onError(errorMessage);
    }
  }

  /**
   * Sync a new drawing that was created externally (e.g., from another device).
   * Downloads the file from S3 and stores locally.
   */
  async syncNewDrawing(newDrawing: DRDrawing) {
    await drawingRepository.createSafeById(newDrawing);
    await this.makeDrawingLocal(newDrawing.id);
  }
}

export const syncer = new Syncer();
